import {
  HsaDynamicOptionSource,
  HsaQuestion,
  HsaQuestionType,
  MULTI_SELECT_NONE_OPTION_VALUE,
  SINGLE_SELECT_OTHER_OPTION_VALUE,
} from "./hsa-question-type";

// Layer 2: the canonical question pool. Slug-keyed registry of authored
// question instances. Each entry is a Layer-1 HsaQuestion bound to specific
// copy and (where applicable) options.
//
// Handles are stable. Once a handle ships, it is never reused — even after the
// pool entry is removed. This protects historical answer rows in
// `hsa_answer` whose `question_slug` values reference past entries.
//
// Mutation policy (convention only, not lint-enforced):
// - Cosmetic copy edits (typo, wording polish) on an existing handle are OK.
// - Semantic changes (regex change, options added/removed, type changed)
//   require a new handle.
//
// To add a question: define a new entry in HSA_QUESTION_POOL below. The
// `satisfies Record<string, HsaQuestion>` clause type-checks the entry
// against the Layer-1 schema at compile time.

/** 1–100 characters, no digit or "@", at least one that isn't a space or name punctuation; linear-time. */
const LEGAL_NAME_PART_REGEX = "^(?=[^0-9@]*[^\\s0-9@.,'’-])[^0-9@]{1,100}$";

// Shared option values referenced by the pool entries below and by the default
// flow's knockout conditions, so the two never drift.
export const SEX_FEMALE_VALUE = "female";
export const YES_OPTION_VALUE = "yes";
export const NO_OPTION_VALUE = "no";

const STATE_OPTIONS: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "Washington D.C." },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// Order and labels match TrueMed's UI verbatim — they presumably tested
// this ordering for conversion. Unknown/Not listed sits at the top as the
// catch-all for "I don't see mine"; "I don't have one" sits at the bottom
// for users with no HSA/FSA at all.
const HSA_ADMINISTRATOR_OPTIONS: { value: string; label: string }[] = [
  { value: "other", label: "Unknown / Not listed" },
  { value: "healthequity", label: "HealthEquity" },
  { value: "wex", label: "WEX" },
  { value: "optum", label: "Optum" },
  { value: "boa", label: "Bank of America" },
  { value: "fidelity", label: "Fidelity" },
  { value: "fsafeds", label: "FSAFEDS" },
  { value: "healthtrust", label: "HealthTrust" },
  { value: "navia", label: "Navia" },
  { value: "lively", label: "Lively" },
  { value: "thatch", label: "Thatch" },
  { value: "peak-one", label: "Peak One" },
  { value: "nuesynergy", label: "NueSynergy" },
  { value: "clarity-benefit-solutions", label: "Clarity Benefit Solutions" },
  { value: "pna-group", label: "P&A Group" },
  { value: "piopac-fidelity", label: "PIOPAC Fidelity" },
  { value: "melody-benefits", label: "Melody Benefits" },
  { value: "rokket-med", label: "RokketMed" },
  { value: "tasc", label: "TASC" },
  { value: "thrive-pass", label: "ThrivePass" },
  { value: "venteur", label: "Venteur" },
  { value: "american-fidelity", label: "American Fidelity" },
  { value: "none", label: "I don't have one" },
];

const MEDICAL_CONDITIONS_OPTIONS: { value: string; label: string }[] = [
  { value: "anxiety_gad", label: "Anxiety (Generalized Anxiety Disorder)" },
  { value: "arthritis", label: "Arthritis" },
  { value: "chronic_fatigue", label: "Chronic Fatigue" },
  { value: "congestive_heart_failure", label: "Congestive Heart Failure" },
  { value: "coronary_artery_disease", label: "Coronary Artery Disease" },
  { value: "depression", label: "Depression" },
  { value: "high_cholesterol", label: "High Cholesterol" },
  { value: "hypertension", label: "Hypertension (High Blood Pressure)" },
  {
    value: "insulin_resistance",
    label: "Insulin Resistance (Includes Pre-diabetes or Gestational Diabetes)",
  },
  { value: "overweight", label: "Overweight" },
  { value: "peripheral_artery_disease", label: "Peripheral Artery Disease" },
  { value: "prediabetes", label: "Prediabetes" },
  { value: "pulmonary_hypertension", label: "Pulmonary Hypertension" },
  { value: "thyroid_disease", label: "Thyroid Disease" },
  {
    value: "thyroid_dysfunction",
    label: "Thyroid Dysfunction (Hypothyroidism or Hyperthyroidism)",
  },
  { value: "type_2_diabetes", label: "Type 2 Diabetes" },
  { value: "varicose_veins", label: "Varicose Veins" },
  { value: MULTI_SELECT_NONE_OPTION_VALUE, label: "None" },
];

// Source of truth for the set of handles in the pool. Defined as a const
// tuple so the type is the union of literal handle strings, and so we can
// iterate the pool without type-casting Object.keys.
export const HSA_QUESTION_HANDLES = [
  "email-address",
  "first-and-last-name",
  "age",
  "sex-assigned-at-birth",
  "hsa-administrator",
  "state-of-residence",
  "medical-conditions-diagnosed",
  "date-of-birth",
  "gender",
  "phone-number",
  "diagnosis",
  "pregnancy",
  "provider-restriction",
  "current-medical-conditions",
  "current-medications",
  "known-allergies",
  "legal-first-name",
  "legal-last-name",
] as const;

export type HsaQuestionHandle = (typeof HSA_QUESTION_HANDLES)[number];

// The pool. Typed as Record<HsaQuestionHandle, HsaQuestion> so the
// compiler enforces that every handle in HSA_QUESTION_HANDLES has a
// matching entry below, and rejects any entry whose key isn't a known
// handle. No `as const` needed.
export const HSA_QUESTION_POOL: Record<HsaQuestionHandle, HsaQuestion> = {
  "email-address": {
    type: HsaQuestionType.EMAIL_INPUT_QUESTION,
    label: "What's your email address?",
    description:
      "Enter the email you'd like us to use for important updates, including your Letter of Medical Necessity",
    placeholder: "Email address",
    required: true,
  },
  "first-and-last-name": {
    type: HsaQuestionType.TEXT_INPUT_QUESTION,
    label: "What's your first and last name?",
    description:
      "Please note: you may not answer for someone else, including dependents",
    placeholder: "First and last name (no abbreviations)",
    required: true,
    validationRegex: ".+\\s.+",
    validationMessage: "Please enter your first and last name",
  },
  age: {
    type: HsaQuestionType.AGE_INPUT_QUESTION,
    label: "What is your age?",
    placeholder: "Age in years",
    required: true,
  },
  "sex-assigned-at-birth": {
    type: HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION,
    label: "What is your sex assigned at birth?",
    required: true,
    options: [
      { value: "female", label: "Female" },
      { value: "male", label: "Male" },
      { value: SINGLE_SELECT_OTHER_OPTION_VALUE, label: "Other" },
    ],
  },
  "hsa-administrator": {
    type: HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION,
    label: "Who manages your HSA/FSA account?",
    placeholder: "Select your administrator",
    required: true,
    options: HSA_ADMINISTRATOR_OPTIONS,
  },
  "state-of-residence": {
    type: HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION,
    label: "What state do you live in?",
    description:
      "Your survey must be reviewed by a licensed provider in your state",
    placeholder: "Select your state",
    required: true,
    options: STATE_OPTIONS,
  },
  "medical-conditions-diagnosed": {
    type: HsaQuestionType.MULTI_SELECT_NONE_QUESTION,
    label: "Have you been diagnosed with any of the following conditions?",
    description:
      "Select all of the medical conditions that you are currently working to mitigate, treat, or cure",
    required: true,
    options: MEDICAL_CONDITIONS_OPTIONS,
  },
  "date-of-birth": {
    type: HsaQuestionType.DATE_INPUT_QUESTION,
    label: "What's your date of birth?",
    required: true,
  },
  gender: {
    type: HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION,
    label: "What is your sex assigned at birth?",
    required: true,
    options: [
      { value: SEX_FEMALE_VALUE, label: "Female" },
      { value: "male", label: "Male" },
      { value: "other", label: "Other" },
    ],
  },
  "phone-number": {
    type: HsaQuestionType.PHONE_INPUT_QUESTION,
    label: "What's your phone number?",
    required: true,
  },
  diagnosis: {
    type: HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION,
    optionSource: HsaDynamicOptionSource.GALE_DIAGNOSIS_CODES,
    label: "Which condition(s) are you seeking treatment for?",
    description:
      "Select the condition(s) the products in your cart help you treat.",
    required: true,
    // Injected at render time from the handoff checkout's ICD-10 codes.
    options: [],
  },
  pregnancy: {
    type: HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION,
    label: "Are you currently pregnant?",
    required: true,
    options: [
      { value: NO_OPTION_VALUE, label: "No" },
      { value: YES_OPTION_VALUE, label: "Yes" },
    ],
  },
  "provider-restriction": {
    type: HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION,
    label:
      "Has a medical provider ever told you not to utilize this therapeutic option?",
    required: true,
    options: [
      { value: NO_OPTION_VALUE, label: "No" },
      { value: YES_OPTION_VALUE, label: "Yes" },
    ],
  },
  // "Yes" doubles as the other-text option (value "other") so choosing it
  // reveals the inline free-text field with the placeholder examples.
  "current-medical-conditions": {
    type: HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION,
    label: "Do you have any current medical conditions?",
    placeholder:
      "Example: Chronic migraines, lower back pain, or trouble breathing due to asthma.",
    required: true,
    otherTextMultiline: true,
    options: [
      { value: NO_OPTION_VALUE, label: "No" },
      { value: SINGLE_SELECT_OTHER_OPTION_VALUE, label: "Yes" },
    ],
  },
  "current-medications": {
    type: HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION,
    label: "Are you currently taking any medications?",
    placeholder:
      "Example: Ibuprofen 200mg twice daily, Metformin 500mg once daily.",
    required: true,
    otherTextMultiline: true,
    options: [
      { value: NO_OPTION_VALUE, label: "No" },
      { value: SINGLE_SELECT_OTHER_OPTION_VALUE, label: "Yes" },
    ],
  },
  "known-allergies": {
    type: HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION,
    label: "Do you have any known allergies?",
    placeholder: "Example: Penicillin, peanuts, pollen, or latex.",
    required: true,
    otherTextMultiline: true,
    options: [
      { value: NO_OPTION_VALUE, label: "No" },
      { value: SINGLE_SELECT_OTHER_OPTION_VALUE, label: "Yes" },
    ],
  },
  // Reimbursement questionnaires ask these instead of "first-and-last-name".
  "legal-first-name": {
    type: HsaQuestionType.TEXT_INPUT_QUESTION,
    label: "What's your legal first name?",
    description:
      "Please note: you may not answer for someone else, including dependents",
    placeholder: "Legal first name",
    required: true,
    validationRegex: LEGAL_NAME_PART_REGEX,
    validationMessage: "Please enter your legal first name",
  },
  "legal-last-name": {
    type: HsaQuestionType.TEXT_INPUT_QUESTION,
    label: "What's your legal last name?",
    description: "Include every part of your last name, as on your ID",
    placeholder: "Legal last name",
    required: true,
    validationRegex: LEGAL_NAME_PART_REGEX,
    validationMessage: "Please enter your legal last name",
  },
};

const HSA_QUESTION_HANDLE_SET: ReadonlySet<string> = new Set(
  HSA_QUESTION_HANDLES,
);

export function isHsaQuestionHandle(value: string): value is HsaQuestionHandle {
  return HSA_QUESTION_HANDLE_SET.has(value);
}

export function getHsaPoolQuestion(handle: HsaQuestionHandle): HsaQuestion {
  return HSA_QUESTION_POOL[handle];
}

// Returns handle + question bundled. Useful when callers (flow walker, MCP
// tree view) want the handle alongside the question payload.
export interface HsaPoolEntry {
  handle: HsaQuestionHandle;
  question: HsaQuestion;
}

export function getHsaPoolEntry(handle: HsaQuestionHandle): HsaPoolEntry {
  return { handle, question: HSA_QUESTION_POOL[handle] };
}

export function listHsaPoolEntries(): HsaPoolEntry[] {
  return HSA_QUESTION_HANDLES.map((handle) => ({
    handle,
    question: HSA_QUESTION_POOL[handle],
  }));
}
